import type { RedeemState } from "@/interfaces/redeem";

/**
 * Copy + visual tone for the public campaign-specific Redeem URL page (`/redeem/:code`). The four
 * message strings are the EXACT copy confirmed with the team (2026-10-26) — do not paraphrase. The
 * page renders them client-side (it has the submitted email; the server supplies the redemption
 * date), so the copy lives here, next to the view, rather than on the wire.
 */

export const REDEEM_SUPPORT_EMAIL = "support@giftsforgood.com";
export const REDEEM_TITLE = "Find Your Gift";
export const REDEEM_SUBTITLE = "Enter the email address where you received your gift invite.";
export const REDEEM_SUBMIT_LABEL = "REDEEM MY GIFT";
export const REDEEM_EMAIL_PLACEHOLDER = "Enter your email address";

/** Visual tone of each state's bordered message box: green (good) or red (problem). */
export const REDEEM_STATE_TONE: Record<RedeemState, "green" | "red"> = {
  sent: "green",
  already_redeemed: "green",
  expired: "red",
  not_found: "red",
};

/**
 * The EXACT message for a state, with `[recipient email address]` / `[redemption date]` filled in.
 * `not_found` is deliberately generic and is
 * also what an invalid/unrecognized campaign code shows.
 */
export function redeemStateMessage(
  state: RedeemState,
  vars: { email: string; redeemedDate?: string },
): string {
  switch (state) {
    case "sent":
      return `Check your inbox! If your email is eligible for a gift, we've sent a link to ${vars.email}. Don't forget to check your spam folder if you don't see it in a few minutes.`;
    case "already_redeemed":
      return `It looks like you already selected your gift on ${vars.redeemedDate ?? "a previous date"}! Please check your inbox (and spam folder) for the tracking details sent from Gifts for Good.`;
    case "expired":
      return "The redemption window for this campaign has closed. Please reach out to the person who sent you this gift to see if it can be reactivated. You can also contact support@giftsforgood.com and our team would be happy to assist.";
    case "not_found":
      return "Oops! Something went wrong on our end. Please refresh the page and try again. If you continue to see this message, please email support@giftsforgood.com and we'll be happy to help.";
  }
}
